import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'white' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap select-none rounded-control transition-[background,box-shadow,color,transform] duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none active:translate-y-px';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-gradient-brand text-white shadow-glow hover:brightness-110',
  secondary: 'bg-transparent text-text border border-border hover:border-border-strong hover:bg-surface-3',
  ghost: 'bg-transparent text-text-2 hover:text-text hover:bg-surface-3',
  danger: 'bg-transparent text-danger border border-danger/40 hover:bg-danger/10',
  white: 'bg-white text-bg hover:bg-[#f3f6ff]',
  link: 'bg-transparent text-link hover:text-link-hover px-0 h-auto',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-[13px]',
  md: 'h-11 px-5 text-sm',
  lg: 'h-13 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, block, leftIcon, rightIcon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(base, variants[variant], variant !== 'link' && sizes[size], block && 'w-full', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});
